import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CHORE_THEMES, fmtMoney } from "@/lib/themes";
import { toast } from "sonner";

const STATUS = {
  todo: { label: "To Do", color: "#64748B", bg: "#F1F5F9" },
  pending: { label: "Pending Approval", color: "#FF7A00", bg: "#FFEDD5" },
  completed: { label: "Completed", color: "#10B981", bg: "#D1FAE5" },
};

export function ChoresTab({ onData }) {
  const { members, activeMember, isParent, refreshMembers } = useAuth();
  const [assignments, setAssignments] = useState([]);
  const [buddyFor, setBuddyFor] = useState(null);

  const load = async () => {
    const { data } = await api.get("/assignments");
    setAssignments(data);
  };
  useEffect(() => { load(); }, []);

  const act = async (fn, msg) => {
    try {
      await fn();
      await load();
      await refreshMembers();
      onData?.();
      if (msg) toast.success(msg);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Something went wrong");
    }
  };

  const complete = (a) => act(() => api.patch(`/assignments/${a.id}/complete`), "Marked done — waiting for a parent 👍");
  const approve = (a) => act(() => api.patch(`/assignments/${a.id}/approve`), "Approved & reward paid! 💰");
  const trade = (a) => act(() => api.post(`/assignments/${a.id}/trade`, { member_id: activeMember.id }), "Chore traded away! 🍫");
  const setBuddy = (a, buddyId) =>
    act(() => api.post(`/assignments/${a.id}/buddy`, { buddy_member_id: buddyId }), "Buddy added — reward will split!");

  const memberName = (id) => members.find((m) => m.id === id)?.name || "?";

  const grouped = members
    .map((m) => ({ member: m, items: assignments.filter((a) => a.member_id === m.id) }))
    .filter((g) => g.items.length > 0);

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-2">
        <div>
          <h2 className="text-2xl font-extrabold" style={{ fontFamily: "Outfit" }}>This Week's Chores</h2>
          <p className="text-slate-500 font-medium">Tick off your jobs. Parents approve to release the reward.</p>
        </div>
        {activeMember && (
          <div className="text-sm font-bold bg-[#EDE9FE] text-[#8B5CF6] rounded-full px-3 py-1.5">
            🍫 {activeMember.brownie_points} brownie pts · trade a chore for 50
          </div>
        )}
      </div>

      {grouped.length === 0 && (
        <div className="card-tactile p-12 text-center text-slate-400 font-medium">
          Nothing assigned yet. Spin the wheel to hand out this week's chores!
        </div>
      )}

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
        {grouped.map(({ member, items }) => (
          <div key={member.id} className="card-tactile p-5">
            <div className="flex items-center gap-2 mb-4">
              <span className="w-9 h-9 rounded-full flex items-center justify-center text-lg"
                style={{ background: member.color + "33" }}>{member.avatar || "🙂"}</span>
              <div className="font-extrabold" style={{ color: member.color }}>{member.name}</div>
              <span className="ml-auto text-xs font-bold text-slate-400">
                {items.filter((i) => i.status === "completed").length}/{items.length} done
              </span>
            </div>
            <div className="space-y-2.5">
              {items.map((a) => {
                const theme = CHORE_THEMES[a.chore?.theme] || CHORE_THEMES.washing_up;
                const st = STATUS[a.status];
                const isMine = activeMember?.id === a.member_id;
                return (
                  <div key={a.id} data-testid="chore-item" className="rounded-xl border-2 border-slate-100 p-3">
                    <div className="flex items-center gap-2">
                      <span className="text-xl">{theme.emojis[0]}</span>
                      <div className="flex-1 min-w-0">
                        <div className="font-bold text-sm truncate">{a.chore?.title}</div>
                        <div className="text-[11px] font-bold" style={{ color: st.color }}>{st.label}
                          {a.buddy_member_id && <> · 🤝 {memberName(a.buddy_member_id)}</>}
                        </div>
                      </div>
                      <div className="font-extrabold text-sm text-emerald-600">{fmtMoney(a.chore?.reward)}</div>
                    </div>
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {isMine && a.status === "todo" && (
                        <>
                          <button data-testid="complete-chore-btn" onClick={() => complete(a)}
                            className="btn-tactile bg-[#10B981] text-white text-xs px-3 py-1.5">Mark done ✓</button>
                          <button onClick={() => setBuddyFor(buddyFor === a.id ? null : a.id)}
                            className="btn-tactile bg-white text-slate-700 text-xs px-3 py-1.5">🤝 Buddy</button>
                          <button data-testid="trade-chore-btn" onClick={() => trade(a)}
                            className="btn-tactile bg-[#8B5CF6] text-white text-xs px-3 py-1.5">Trade 🍫50</button>
                        </>
                      )}
                      {isParent && a.status === "pending" && (
                        <button data-testid="approve-chore-btn" onClick={() => approve(a)}
                          className="btn-tactile bg-[#FF7A00] text-white text-xs px-3 py-1.5">Approve & pay 💰</button>
                      )}
                      {a.status === "completed" && <span className="text-xs font-bold text-emerald-600">🎉 Reward paid</span>}
                    </div>
                    {buddyFor === a.id && (
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        {members.filter((m) => m.id !== a.member_id).map((m) => (
                          <button key={m.id} onClick={() => { setBuddy(a, m.id); setBuddyFor(null); }}
                            className="text-xs font-bold rounded-full px-2.5 py-1 border-2"
                            style={{ borderColor: m.color, color: m.color }}>+ {m.name}</button>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
